import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createOperatingRoomSchedule, getOperatingRooms, getHospitals, Hospital, OperatingRoom, importOperatingRoomSchedules, ScheduleImportResult } from '../services/api.ts';
import { localDateTimeToUtc, getLocalDateString } from '../utils/timezone.ts';
import AdminOnly from './AdminOnly.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import SpreadsheetImport, { ImportFieldDef, RowOutcome } from './SpreadsheetImport.tsx';
import { normToken, resolveRoomByName, toUtcFromDateAndTime, NamedRoom } from './importUtils.ts';

interface CaseImportValue {
  operatingRoomId: string;
  startTimeUtc: string;
  endTimeUtc: string;
  surgeryType: string;
}

const CASE_IMPORT_FIELDS: ImportFieldDef[] = [
  { key: 'room', label: 'Operating Room', required: true, aliases: ['room', 'or', 'operating room', 'suite', 'location', 'room name'], hint: 'Must match a room name on the Hospitals & ORs page' },
  { key: 'hospital', label: 'Hospital / Site', required: false, aliases: ['hospital', 'site', 'facility', 'campus', 'location'], hint: 'Optional — only needed if two sites share a room name' },
  { key: 'start', label: 'Start Time', required: true, aliases: ['start', 'start time', 'begin', 'wheels in', 'in', 'case start', 'scheduled start'], hint: 'e.g. 7:00, 07:00, 7:00 AM, 0700' },
  { key: 'end', label: 'End Time', required: true, aliases: ['end', 'end time', 'finish', 'wheels out', 'out', 'case end', 'scheduled end'] },
  { key: 'surgery', label: 'Procedure / Surgery', required: false, aliases: ['procedure', 'surgery', 'case', 'description', 'service', 'type', 'operation'] },
];

const OperatingRoomScheduleForm: React.FC = () => {
  const [formData, setFormData] = useState({
    operatingRoomId: '',
    startTimeUtc: '',
    endTimeUtc: '',
    surgeryType: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [operatingRooms, setOperatingRooms] = useState<OperatingRoom[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [importDate, setImportDate] = useState<string>(getLocalDateString());

  // Import state
  const [importing, setImporting] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [importResult, setImportResult] = useState<ScheduleImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [roomsResp, hospResp] = await Promise.all([getOperatingRooms(), getHospitals()]);
        setOperatingRooms(roomsResp.operatingRooms || []);
        setHospitals(hospResp.hospitals || []);
      } catch (error) {
        console.error('Error fetching reference data:', error);
        setMessage({ type: 'error', text: 'Failed to load operating rooms' });
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchReferenceData();
  }, []);

  // Map normalized hospital name -> id, so an optional "Hospital" column can disambiguate rooms.
  const hospitalIdByName = useMemo(() => {
    const m = new Map<string, string>();
    hospitals.forEach(h => m.set(normToken(h.name), h.hospitalId));
    return m;
  }, [hospitals]);

  const namedRooms: NamedRoom[] = useMemo(
    () => operatingRooms.map(r => ({ operatingRoomId: r.operatingRoomId, name: r.name, roomNumber: r.roomNumber, hospitalId: r.hospitalId })),
    [operatingRooms]
  );

  // Turn one mapped spreadsheet row into a schedule to create (or an error explaining why not).
  const transformCaseRow = (mapped: Record<string, string>): RowOutcome<CaseImportValue> => {
    const roomRes = resolveRoomByName(namedRooms, hospitalIdByName, mapped.room, mapped.hospital);
    if (!roomRes.ok) return { ok: false, error: roomRes.error };
    const startTimeUtc = toUtcFromDateAndTime(importDate, mapped.start);
    if (!startTimeUtc) return { ok: false, error: `couldn't read start time "${mapped.start}"` };
    const endTimeUtc = toUtcFromDateAndTime(importDate, mapped.end);
    if (!endTimeUtc) return { ok: false, error: `couldn't read end time "${mapped.end}"` };
    if (new Date(endTimeUtc).getTime() <= new Date(startTimeUtc).getTime()) {
      return { ok: false, error: 'end time is not after start time' };
    }
    return {
      ok: true,
      value: {
        operatingRoomId: roomRes.room.operatingRoomId,
        startTimeUtc,
        endTimeUtc,
        surgeryType: mapped.surgery?.trim() || 'Case',
      },
    };
  };

  // Create the schedules one at a time and report how many landed.
  const commitCases = async (values: CaseImportValue[]): Promise<string> => {
    let created = 0;
    const failures: string[] = [];
    for (const v of values) {
      try {
        await createOperatingRoomSchedule(v);
        created += 1;
      } catch (err: any) {
        failures.push(err?.response?.data?.error || err?.message || 'unknown error');
      }
    }
    if (created === 0) throw new Error(failures[0] || 'No cases could be created.');
    let msg = `Imported ${created} case${created === 1 ? '' : 's'} for ${importDate}.`;
    if (failures.length > 0) msg += ` ${failures.length} could not be created.`;
    return msg;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      // Convert local times to UTC before sending to backend
      const submitData = {
        ...formData,
        startTimeUtc: localDateTimeToUtc(formData.startTimeUtc),
        endTimeUtc: localDateTimeToUtc(formData.endTimeUtc)
      };

      await createOperatingRoomSchedule(submitData);
      setMessage({ type: 'success', text: 'Operating room schedule created successfully!' });
      setFormData({
        operatingRoomId: '',
        startTimeUtc: '',
        endTimeUtc: '',
        surgeryType: ''
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to create operating room schedule'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name.includes('Required') ? parseInt(value) || 0 : value
    });
  };

  const handleImportFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const jsonpData = e.target?.result as string;
          const result = await importOperatingRoomSchedules({ jsonpData, dryRun });
          setImportResult(result);
        } catch (error) {
          console.error('Error importing schedules:', error);
          setImportResult({
            success: false,
            schedulesCreated: 0,
            schedulesSkipped: 0,
            totalRowsParsed: 0,
            unmappedLocations: [],
            unmappedRooms: [],
            errors: ['Failed to import schedules. Check console for details.']
          });
        } finally {
          setImporting(false);
        }
      };

      reader.onerror = () => {
        console.error('Error reading file');
        setImporting(false);
      };

      reader.readAsText(file);
    } catch (error) {
      console.error('Error processing file:', error);
      setImporting(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <ReadOnlyBanner />

      {/* Generic spreadsheet import (Excel / CSV) */}
      <AdminOnly>
        <SpreadsheetImport<CaseImportValue>
          title="Import cases from a spreadsheet"
          description="Upload an Excel (.xlsx) or CSV file of the day's cases — for example a MEDAXION, EPIC, OR Control, or plain Excel export saved as a spreadsheet. Match your columns below, review the preview, then import. Rooms are matched by name to the Hospitals & ORs page."
          fields={CASE_IMPORT_FIELDS}
          transformRow={transformCaseRow}
          onCommit={commitCases}
          ready={!loadingRooms}
          notReadyMessage="Loading operating rooms…"
          extraControls={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <label htmlFor="caseImportDate" style={{ margin: 0, whiteSpace: 'nowrap' }}>Date for these cases:</label>
              <input
                id="caseImportDate"
                type="date"
                className="form-control"
                value={importDate}
                onChange={(e) => setImportDate(e.target.value)}
                style={{ maxWidth: '190px' }}
              />
              <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>
                Start/end times in the file are combined with this date (local time).
              </span>
            </div>
          }
        />
      </AdminOnly>

      {/* Advanced: OR Control JSONP import (legacy path) */}
      <AdminOnly>
      <details className="card" style={{ marginBottom: '16px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Advanced: import an OR Control JSONP file</summary>
        <p style={{ fontSize: '0.9em', color: 'var(--text-muted)', margin: '12px 0' }}>
          Upload a Google Visualization JSONP response file to import OR schedules.
          Make sure location and room mappings are configured first.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry Run (validate without writing)
          </label>

          <button
            onClick={handleImportButtonClick}
            disabled={importing}
            style={{
              padding: '8px 16px',
              backgroundColor: importing ? 'var(--bg-surface-hover)' : dryRun ? 'var(--or-blue)' : 'var(--success)',
              color: importing ? 'var(--text-muted)' : 'var(--labcoat)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: importing ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {importing ? 'Importing...' : dryRun ? 'Validate File' : 'Import Schedules'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".js,.json,.txt"
            onChange={handleImportFileUpload}
            style={{ display: 'none' }}
          />
        </div>

        {importResult && (
          <div style={{
            padding: '12px',
            borderRadius: '6px',
            backgroundColor: importResult.success && importResult.errors.length === 0 ? 'rgba(22, 163, 74, 0.18)' : 'rgba(217, 119, 6, 0.18)',
            border: `1px solid ${importResult.success && importResult.errors.length === 0 ? 'rgba(22, 163, 74, 0.4)' : 'rgba(217, 119, 6, 0.4)'}`,
            color: importResult.success && importResult.errors.length === 0 ? 'var(--success-text)' : 'var(--warning-text)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              {dryRun ? 'Dry Run Results' : 'Import Results'}
            </div>
            <div style={{ fontSize: '0.9em' }}>
              <div>Total rows parsed: {importResult.totalRowsParsed}</div>
              <div>Schedules created: {importResult.schedulesCreated}</div>
              <div>Schedules skipped (already exist): {importResult.schedulesSkipped}</div>
            </div>

            {importResult.unmappedLocations.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <strong>Unmapped Locations ({importResult.unmappedLocations.length}):</strong>
                <ul style={{ marginTop: '4px', marginBottom: '0', paddingLeft: '20px' }}>
                  {importResult.unmappedLocations.map((loc, idx) => (
                    <li key={idx} style={{ fontSize: '0.85em' }}>{loc}</li>
                  ))}
                </ul>
              </div>
            )}

            {importResult.unmappedRooms.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <strong>Unmapped Rooms ({importResult.unmappedRooms.length}):</strong>
                <ul style={{ marginTop: '4px', marginBottom: '0', paddingLeft: '20px' }}>
                  {importResult.unmappedRooms.slice(0, 20).map((room, idx) => (
                    <li key={idx} style={{ fontSize: '0.85em' }}>{room}</li>
                  ))}
                  {importResult.unmappedRooms.length > 20 && (
                    <li style={{ fontSize: '0.85em', fontStyle: 'italic' }}>
                      ... and {importResult.unmappedRooms.length - 20} more
                    </li>
                  )}
                </ul>
              </div>
            )}

            {importResult.errors.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <strong>Errors ({importResult.errors.length}):</strong>
                <ul style={{ marginTop: '4px', marginBottom: '0', paddingLeft: '20px' }}>
                  {importResult.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx} style={{ fontSize: '0.85em' }}>{err}</li>
                  ))}
                  {importResult.errors.length > 5 && (
                    <li style={{ fontSize: '0.85em', fontStyle: 'italic' }}>
                      ... and {importResult.errors.length - 5} more
                    </li>
                  )}
                </ul>
              </div>
            )}

            <button
              onClick={() => setImportResult(null)}
              style={{
                marginTop: '8px',
                padding: '4px 8px',
                fontSize: '0.8em',
                backgroundColor: 'transparent',
                color: 'inherit',
                border: '1px solid currentColor',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Dismiss
            </button>
          </div>
        )}
      </details>
      </AdminOnly>

      {/* Manual Create Section */}
      <AdminOnly>
      <div className="card">
        <h2>Create Operating Room Schedule</h2>
        {message && (
          <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="operatingRoomId">Operating Room</label>
            <select
              id="operatingRoomId"
              name="operatingRoomId"
              value={formData.operatingRoomId}
              onChange={handleChange}
              required
              disabled={loadingRooms}
            >
              <option value="">
                {loadingRooms ? 'Loading operating rooms...' : 'Select operating room'}
              </option>
              {operatingRooms.map((room) => (
                <option key={room.operatingRoomId} value={room.operatingRoomId}>
                  {room.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="startTimeUtc">Start Time (Local Time)</label>
            <input
              type="datetime-local"
              id="startTimeUtc"
              name="startTimeUtc"
              value={formData.startTimeUtc}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="endTimeUtc">End Time (Local Time)</label>
            <input
              type="datetime-local"
              id="endTimeUtc"
              name="endTimeUtc"
              value={formData.endTimeUtc}
              onChange={handleChange}
              required
            />
          </div>


          <div className="form-group">
            <label htmlFor="surgeryType">Surgery Type</label>
            <select
              id="surgeryType"
              name="surgeryType"
              value={formData.surgeryType}
              onChange={handleChange}
              required
            >
              <option value="">Select surgery type</option>
              <option value="cardiac">Cardiac</option>
              <option value="orthopedic">Orthopedic</option>
              <option value="neurological">Neurological</option>
              <option value="general">General</option>
              <option value="plastic">Plastic</option>
              <option value="urological">Urological</option>
              <option value="gynecological">Gynecological</option>
              <option value="pediatric">Pediatric</option>
              <option value="emergency">Emergency</option>
              <option value="other">Other</option>
            </select>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Schedule'}
          </button>
        </form>
      </div>
      </AdminOnly>
    </div>
  );
};

export default OperatingRoomScheduleForm;
